import clsx from "clsx"
import { forwardRef, useEffect, useRef, useState } from "react"
import { Controller, type FieldValues } from "react-hook-form"
import { Attachment2 } from "@/shared/components/ui/icons"
import { tCommon, tError } from "@/shared/config/i18n"
import type { CompoundedComponent, IFormControl } from "@/shared/types"
import { Notice } from "@/shared/utils/notice.tsx"

interface IProps {
  label?: string
  onUpload?: (file: File) => void
  accept?: string
  required?: boolean
  error?: string
  readOnly?: boolean
  title?: string
  maxSizeMB?: number
  value?: any
}

const FileUploadInput = forwardRef<HTMLInputElement, IProps>(
  ({ label, title, readOnly, onUpload, value, accept = ".doc,.docx,.xls,.xlsx,.pdf", required = false, error, maxSizeMB }: IProps, ref) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [fileName, setFileName] = useState<string | null>(value || null)

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file && onUpload) {
        if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
          Notice.error(`${tError("form-error-message.max-file-size")} ${maxSizeMB} MB`)
          return
        }

        setFileName(file.name)
        onUpload(file)
      }
    }

    const triggerFileUpload = () => {
      fileInputRef.current?.click()
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        triggerFileUpload()
      }
    }

    useEffect(() => {
      if (typeof window !== "undefined" && typeof File === "function" && value instanceof File) {
        setFileName(value.name)
      } else if (typeof value === "string") {
        setFileName(value)
      } else {
        setFileName(null)
      }
    }, [value])

    return (
      <div className="flex flex-col w-full gap-2 p-1 -m-1 overflow-hidden" title={title}>
        <span className="flex h-[9px] items-center text-[13px]">
          {label}
          {(error || required) && <strong className="ml-0.5 text-[10px] text-red-500">＊</strong>}
        </span>
        <div
          className="flex rounded-lg outline-none focus:ring-1 focus:ring-blue-600"
          tabIndex={0}
          role="button"
          onKeyDown={handleKeyDown}
          onClick={triggerFileUpload}
        >
          <div className="flex items-center flex-1 gap-2 px-4 py-3 overflow-hidden duration-75 border rounded-tl-lg rounded-bl-lg cursor-pointer border-border hover:bg-ui-background-muted active:bg-ui-background-muted">
            {!fileName && <Attachment2 size={20} className="h-[17px] text-DarkGray" />}
            <span
              title={fileName ? fileName : tCommon("select-file")}
              className={clsx(
                "h-[17px] select-none overflow-hidden text-ellipsis text-nowrap text-sm",
                fileName ? "text-blue-600 underline underline-offset-4" : "text-DarkGray"
              )}
              children={fileName ? fileName : tCommon("select-file")}
            />
          </div>
          <span className="p-3 border rounded-tr-lg rounded-br-lg cursor-pointer select-none border-border bg-border" children={tCommon("select")} />
          <input readOnly={readOnly} ref={fileInputRef} type="file" accept={accept} className="hidden" onChange={handleFileChange} />
        </div>
        {error && <span className="text-[10px] text-red-500">{error}</span>}
      </div>
    )
  }
) as CompoundedComponent<IProps, HTMLInputElement, { Form: typeof FormFileUploadInput }>

const FormFileUploadInput = <T extends FieldValues>({
  control,
  name,
  rules,
  required,
  label,
  onUpload,
  maxSizeMB,
  ...props
}: IFormControl<T> & IProps) => {
  return (
    <Controller
      {...{
        control,
        name,
        rules: {
          required: { value: required ?? false, message: tError("required_field") },
          validate: {
            max: (file: File) => {
              if (maxSizeMB) {
                return file ? file?.size <= maxSizeMB * 1024 * 1024 || `Максимальный размер файла — ${maxSizeMB}MB` : true
              }
            }
          },
          ...rules
        }
      }}
      render={({ field, fieldState }) => {
        const handleFileChange = (file: File) => {
          if (onUpload) {
            if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
              Notice.error(`${tError("form-error-message.max-file-size")} ${maxSizeMB} MB`)
              return
            }

            onUpload(file)
          }
          field.onChange(file)
        }
        return (
          <FileUploadInput
            {...field}
            onUpload={handleFileChange}
            error={fieldState.error?.message}
            label={label}
            maxSizeMB={maxSizeMB}
            required={required}
            {...props}
          />
        )
      }}
    />
  )
}

FileUploadInput.Form = FormFileUploadInput
export default FileUploadInput
